import { useEffect, useState, useCallback } from "react";
import { useBackState } from "@/hooks/useScrollMemory";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { ScanField } from "@/components/ScanField";
import { toast } from "sonner";
import { formatShort } from "@/lib/datetime";
import { scanAccept, scanRefuse } from "@/lib/scanFeedback";
import {
  Recycle, Plus, DollarSign, Lock, PackageOpen, CheckCircle2, ExternalLink,
  Loader2, Boxes, History, Pencil,
} from "lucide-react";

const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// ---------------------------------------------------------------- New batch dialog
function NewBatchDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [material, setMaterial] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/recycle-batches", { material: material.trim() || null, note: note.trim() || null });
      toast.success(`Opened ${data.batch_number}`);
      setOpen(false); setMaterial(""); setNote(""); onCreated?.(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button data-testid="rcy-new-batch-btn" onClick={() => setOpen(true)} className="rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white h-11">
        <Plus className="w-4 h-4 mr-1.5" />Open a recycle batch
      </Button>
      <DialogContent className="rounded-xl border-slate-200 sm:max-w-md" data-testid="rcy-new-batch-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Recycle className="w-5 h-5 text-emerald-600" />Open a recycle batch</DialogTitle>
          <DialogDescription>Start a bulk batch, scan scrap parts into it over time, then close it with the one lump-sum the load pays.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Material (optional)</Label>
            <Input data-testid="rcy-new-material" value={material} onChange={(e) => setMaterial(e.target.value)} placeholder="e.g. steel, aluminum, cast" className="rounded-lg border-slate-200 mt-1" />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Note (optional)</Label>
            <Textarea data-testid="rcy-new-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. north bin, scrap steel run" className="rounded-lg border-slate-200 mt-1" />
          </div>
        </div>
        <DialogFooter>
          <Button data-testid="rcy-new-confirm" onClick={submit} disabled={busy} className="rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white">{busy ? "Opening…" : "Open batch"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------- Close batch dialog (pricing-gated)
function CloseBatchDialog({ batch, onClosed }) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState("");
  const [buyer, setBuyer] = useState("");
  const [material, setMaterial] = useState(batch.material || "");
  const [weight, setWeight] = useState(batch.weight ?? "");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    const val = parseFloat(value);
    if (isNaN(val) || val < 0) { toast.error("Enter the bulk recovery value (0 or more)."); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/recycle-batches/${batch.id}/close`, {
        bulk_recovery_value: val, buyer: buyer.trim() || null,
        material: material.trim() || null, weight: weight === "" ? null : parseFloat(weight),
      });
      toast.success(`${batch.batch_number} closed — ${money(val)} recorded`);
      setOpen(false); onClosed?.(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button data-testid="rcy-close-btn" onClick={() => setOpen(true)} className="rounded-lg bg-[#1F1F1F] hover:bg-black text-white h-11">
        <DollarSign className="w-4 h-4 mr-1.5" />Close & record value
      </Button>
      <DialogContent className="rounded-xl border-slate-200 sm:max-w-md" data-testid="rcy-close-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><DollarSign className="w-5 h-5 text-emerald-600" />Close {batch.batch_number}</DialogTitle>
          <DialogDescription>The money event: record the ONE lump-sum the load paid. This is shop recycling revenue — it is never split back to donor trucks. {batch.part_count} part(s) in this batch.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Bulk recovery value *</Label>
            <div className="relative mt-1">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-mono">$</span>
              <Input data-testid="rcy-close-value" type="number" min="0" step="0.01" value={value} onChange={(e) => setValue(e.target.value)} placeholder="0.00" className="rounded-lg border-slate-200 pl-7 font-mono h-12 text-lg" />
            </div>
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Buyer (optional)</Label>
            <Input data-testid="rcy-close-buyer" value={buyer} onChange={(e) => setBuyer(e.target.value)} placeholder="e.g. ABC Scrap Metal" className="rounded-lg border-slate-200 mt-1" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Material</Label>
              <Input data-testid="rcy-close-material" value={material} onChange={(e) => setMaterial(e.target.value)} placeholder="steel…" className="rounded-lg border-slate-200 mt-1" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Weight (lb)</Label>
              <Input data-testid="rcy-close-weight" type="number" min="0" step="0.1" value={weight} onChange={(e) => setWeight(e.target.value)} placeholder="—" className="rounded-lg border-slate-200 mt-1 font-mono" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button data-testid="rcy-close-confirm" onClick={submit} disabled={busy} className="rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white">{busy ? "Closing…" : "Close batch"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------- Correct value dialog (append-only)
function CorrectValueDialog({ batch, onCorrected }) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(String(batch.bulk_recovery_value ?? ""));
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    const val = parseFloat(value);
    if (isNaN(val) || val < 0) { toast.error("Enter a corrected value (0 or more)."); return; }
    if (reason.trim().length < 3) { toast.error("A reason is required for a value correction."); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/recycle-batches/${batch.id}/correct-value`, { bulk_recovery_value: val, reason: reason.trim() });
      toast.success(`Value corrected to ${money(val)}`);
      setOpen(false); setReason(""); onCorrected?.(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button data-testid="rcy-correct-btn" variant="outline" onClick={() => setOpen(true)} className="rounded-lg border-slate-300 h-9 text-xs">
        <Pencil className="w-3.5 h-3.5 mr-1.5" />Correct value
      </Button>
      <DialogContent className="rounded-xl border-slate-200 sm:max-w-md" data-testid="rcy-correct-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Pencil className="w-5 h-5 text-amber-600" />Correct {batch.batch_number} value</DialogTitle>
          <DialogDescription>Append-only correction. The prior value ({money(batch.bulk_recovery_value)}) is preserved in the batch history; the shop total always reflects the current figure once.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Corrected value *</Label>
            <div className="relative mt-1">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-mono">$</span>
              <Input data-testid="rcy-correct-value" type="number" min="0" step="0.01" value={value} onChange={(e) => setValue(e.target.value)} className="rounded-lg border-slate-200 pl-7 font-mono h-12 text-lg" />
            </div>
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Reason *</Label>
            <Textarea data-testid="rcy-correct-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. buyer reweighed the load" className="rounded-lg border-slate-200 mt-1" />
          </div>
        </div>
        <DialogFooter>
          <Button data-testid="rcy-correct-confirm" onClick={submit} disabled={busy} className="rounded-lg bg-amber-600 hover:bg-amber-700 text-white">{busy ? "Saving…" : "Save correction"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------- Batch detail panel
function BatchDetail({ batchId, canScrap, canPrice, canCost, onChanged }) {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanBusy, setScanBusy] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/recycle-batches/${batchId}`).then(({ data }) => setD(data)).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail))).finally(() => setLoading(false));
  }, [batchId]);
  useEffect(() => { load(); }, [load]);

  const addPart = async (code) => {
    setScanBusy(true);
    try {
      const { data } = await api.post(`/recycle-batches/${batchId}/add`, { part_scan: code });
      scanAccept(`${data.part.short_code} RECYCLED`);
      toast.success(`\u267b ${data.part.short_code} recycled into ${data.batch_number}`);
      load(); onChanged?.();
    } catch (e) { scanRefuse(formatApiErrorDetail(e.response?.data?.detail)); toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setScanBusy(false); }
  };

  if (loading || !d) return <div className="p-10 text-center text-slate-400 font-mono text-sm" data-testid="rcy-detail-loading"><Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />Loading batch…</div>;
  const b = d.batch;
  const isOpen = b.status === "open";
  const corrections = b.value_corrections || [];

  return (
    <div data-testid="rcy-batch-detail" className="space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="font-display text-xl font-black text-slate-900">{b.batch_number}</h2>
            <Badge className={`rounded-md text-[10px] uppercase font-mono ${isOpen ? "bg-emerald-600 text-white" : "bg-zinc-500 text-white"}`}>{isOpen ? "Open" : "Closed"}</Badge>
          </div>
          <p className="text-sm text-slate-500 mt-0.5">
            {b.material ? <span className="font-medium text-slate-700">{b.material}</span> : "No material set"}
            {" · "}{d.part_count} part{d.part_count === 1 ? "" : "s"} · {d.donor_count} donor{d.donor_count === 1 ? "" : "s"}
          </p>
          {b.note && <p className="text-xs text-slate-400 mt-1">{b.note}</p>}
        </div>
        {isOpen && canPrice && <CloseBatchDialog batch={b} onClosed={() => { load(); onChanged?.(); }} />}
      </div>

      {/* Money summary (cost-walled) */}
      {!isOpen && (
        <div data-testid="rcy-money-summary" className="rounded-xl border border-emerald-100 bg-emerald-50/50 p-4">
          {canCost ? (
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <div className="text-[10px] uppercase tracking-wide text-emerald-700 font-bold">Bulk recovery value · shop recycling revenue</div>
                <div className="text-3xl font-display font-black text-emerald-700" data-testid="rcy-batch-value">{money(b.bulk_recovery_value)}</div>
                <div className="text-xs text-slate-500 mt-0.5">
                  {b.buyer ? <>Buyer: <span className="font-medium text-slate-700">{b.buyer}</span> · </> : null}
                  {b.weight != null ? <>{b.weight} lb · </> : null}
                  Closed {formatShort(b.closed_at)} by {b.closed_by}
                </div>
              </div>
              {canPrice && <CorrectValueDialog batch={b} onCorrected={() => { load(); onChanged?.(); }} />}
            </div>
          ) : (
            <div className="flex items-center gap-2 text-sm text-slate-500"><Lock className="w-4 h-4" />Recovery value is hidden — needs cost-view permission.</div>
          )}
          {canCost && corrections.length > 0 && (
            <div className="mt-3 border-t border-emerald-100 pt-2" data-testid="rcy-corrections">
              <div className="text-[10px] uppercase tracking-wide text-slate-400 font-bold flex items-center gap-1 mb-1"><History className="w-3 h-3" />Value corrections</div>
              {corrections.map((c, i) => (
                <div key={`${c.at}-${i}`} className="text-xs text-slate-500 font-mono">{money(c.from)} → {money(c.to)} · {c.reason} · {c.by} · {formatShort(c.at)}</div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Scan-in (open batches, scrap perm) */}
      {isOpen && canScrap && (
        <div data-testid="rcy-scan-in" className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex items-center gap-2 mb-2">
            <PackageOpen className="w-4 h-4 text-emerald-600" />
            <h3 className="font-display font-bold text-sm uppercase tracking-wide text-slate-700">Scan parts into this batch</h3>
          </div>
          <ScanField onScan={addPart} testid="rcy-add" busy={scanBusy} continuous submitLabel="Add" placeholder="Scan or type a part tag…" />
          <p className="text-[11px] text-slate-400 mt-2">Each part is scrapped as recycled ($0 to its donor) and stays visible on its donor truck forever.</p>
        </div>
      )}

      {/* Parts in batch */}
      <div data-testid="rcy-parts" className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex items-center gap-2 mb-3">
          <Boxes className="w-4 h-4 text-slate-400" />
          <h3 className="font-display font-bold text-sm uppercase tracking-wide text-slate-700">Parts in this batch ({d.part_count})</h3>
        </div>
        {d.parts.length === 0 ? (
          <p className="text-sm text-slate-400">No parts yet — scan some in above.</p>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {d.parts.map((p) => (
              <Link key={p.id} to={`/parts/${p.id}`} data-testid={`rcy-part-${p.short_code}`}
                className="inline-flex items-center gap-1 text-xs font-mono px-2 py-1 rounded-md border border-slate-200 hover:border-emerald-500 hover:bg-emerald-50 transition-colors">
                {p.short_code}{p.name ? <span className="text-slate-400">· {p.name}</span> : null}
                <ExternalLink className="w-3 h-3 text-slate-400" />
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- Main page
export default function RecycleBatchesPage() {
  const { has } = useAuth();
  const canScrap = has("scrap_writeoff");
  const canPrice = has("set_pricing");
  const canCost = has("view_acquisition_cost");
  const [batches, setBatches] = useState(null);
  const [stats, setStats] = useState(null);
  const [selId, setSelId] = useBackState("selId", null);

  const loadBatches = useCallback(() => {
    api.get("/recycle-batches").then(({ data }) => {
      setBatches(data.batches || []);
      setSelId((cur) => cur || (data.batches?.[0]?.id ?? null));
    }).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
    api.get("/recycle-batches/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);
  useEffect(() => { loadBatches(); }, [loadBatches]);

  const onChanged = () => loadBatches();
  const onCreated = (b) => { loadBatches(); setSelId(b.id); };

  return (
    <div data-testid="recycle-batches-page" className="space-y-5">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-black tracking-tight text-slate-900 flex items-center gap-2">
            <Recycle className="w-7 h-7 text-emerald-600" />Recycling Batches
          </h1>
          <p className="text-sm text-slate-500 mt-1">Gather scrap into bulk batches, then close with the one lump-sum the load pays — a shop revenue pool, kept separate from donor recovery.</p>
        </div>
        {canScrap && <NewBatchDialog onCreated={onCreated} />}
      </div>

      {/* Revenue stat strip (cost-walled) */}
      {stats && (
        <div data-testid="rcy-stats-strip" className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
            <div className="text-[11px] uppercase tracking-wide text-slate-400 font-bold">Open batches</div>
            <div className="text-2xl font-display font-black text-emerald-600" data-testid="rcy-stat-open">{stats.open_batches}</div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
            <div className="text-[11px] uppercase tracking-wide text-slate-400 font-bold">Closed batches</div>
            <div className="text-2xl font-display font-black text-slate-700" data-testid="rcy-stat-closed">{stats.closed_batches}</div>
          </div>
          {canCost && (
            <div className="bg-white rounded-xl border border-emerald-100 p-4 shadow-sm">
              <div className="text-[11px] uppercase tracking-wide text-emerald-700 font-bold flex items-center gap-1"><DollarSign className="w-3 h-3" />Recycling revenue</div>
              <div className="text-2xl font-display font-black text-emerald-700" data-testid="rcy-stat-revenue">{money(stats.recycling_revenue)}</div>
            </div>
          )}
        </div>
      )}

      <div className="lg:flex lg:gap-6 lg:items-start">
        {/* Batch list */}
        <aside data-testid="rcy-batch-list" className="lg:w-[300px] shrink-0 space-y-2 mb-4 lg:mb-0">
          {batches == null ? (
            <div className="p-6 text-center text-slate-400 font-mono text-sm"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
          ) : batches.length === 0 ? (
            <div className="border border-dashed border-slate-300 rounded-xl bg-slate-50/50 p-6 text-center text-sm text-slate-500">
              No recycle batches yet.{canScrap ? " Open one to get started." : ""}
            </div>
          ) : batches.map((b) => (
            <button key={b.id} data-testid={`rcy-list-${b.batch_number}`} onClick={() => setSelId(b.id)}
              className={`w-full text-left rounded-xl border p-3 transition-all ${selId === b.id ? "border-emerald-500 bg-emerald-50 ring-1 ring-emerald-200" : "border-slate-200 bg-white hover:border-slate-300"}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="font-mono font-bold text-sm text-slate-800">{b.batch_number}</span>
                {b.status === "open"
                  ? <Badge className="rounded-md text-[9px] uppercase font-mono bg-emerald-600 text-white">Open</Badge>
                  : <Badge className="rounded-md text-[9px] uppercase font-mono bg-zinc-500 text-white"><CheckCircle2 className="w-3 h-3 mr-0.5" />Closed</Badge>}
              </div>
              <div className="text-xs text-slate-500 mt-1">{b.material || "—"} · {b.part_count} part{b.part_count === 1 ? "" : "s"}</div>
              {b.status === "closed" && canCost && b.bulk_recovery_value != null && (
                <div className="text-xs font-mono font-bold text-emerald-700 mt-0.5">{money(b.bulk_recovery_value)}</div>
              )}
            </button>
          ))}
        </aside>

        {/* Detail */}
        <div className="flex-1 min-w-0">
          {selId ? <BatchDetail batchId={selId} canScrap={canScrap} canPrice={canPrice} canCost={canCost} onChanged={onChanged} />
            : <div className="border border-dashed border-slate-300 rounded-xl bg-slate-50/50 p-10 text-center text-sm text-slate-400">Pick a batch on the left to view it.</div>}
        </div>
      </div>
    </div>
  );
}
